import React, { useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputAdornment,
  Typography,
  TextField,
  Paper,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear"; // крестик

interface MainBodyFilterProps {
  onApplyFilters: (filterString: string) => void;
}

const MainBodyFilter: React.FC<MainBodyFilterProps> = ({ onApplyFilters }) => {
  const [notActive, setNotActive] = useState(false);
  const [query, setQuery] = useState("");
  // ✅ Состояния чекбоксов сервисов
  const [services, setServices] = useState({
    media: false,
    portal: false,
    dune: false,
  });
  const [allServices, setAllServices] = useState(false);

  const handleServiceChange = (name: keyof typeof services) => {
    setServices((prev) => {
      const updated = { ...prev, [name]: !prev[name] };

      // если хоть один выключен — снимаем галочку "все"
      const allSelected = Object.values(updated).every(Boolean);
      setAllServices(allSelected);

      // После обновления сразу вызываем handleApply с новыми значениями
      // но делаем это через setTimeout, чтобы дождаться обновления состояния
      setTimeout(() => {
        handleApplyWith(updated);
      }, 0);

      return updated;
    });
  };

  const handleAllServicesChange = () => {
    const newState = !allServices;
    setAllServices(newState);
    const updated = {
      media: newState,
      portal: newState,
      dune: newState,
    };
    setServices(updated);
    setTimeout(() => handleApplyWith(updated), 0);
  };

  // Вспомогательная функция, чтобы можно было передавать обновлённое состояние
  const handleApplyWith = (currentServices = services) => {
    let filterString = "";
    filterString += `notactive=${notActive ? 1 : 0}`;
    if (query.trim()) {
      filterString += `&query=${query.trim()}`;
    }

    filterString += `&service_media=${currentServices.media ? 1 : 0}`;
    filterString += `&service_portal=${currentServices.portal ? 1 : 0}`;
    filterString += `&service_dune=${currentServices.dune ? 1 : 0}`;

    onApplyFilters(filterString);
  };

  const handleApply = () => handleApplyWith();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleApply();
    }
  };

  const handleClear = () => {
    setQuery("");

    // Формируем фильтр без поиска, но с текущими состояниями чекбоксов
    const filterString =
      `notactive=${notActive ? 1 : 0}` +
      `&service_media=${services.media ? 1 : 0}` +
      `&service_portal=${services.portal ? 1 : 0}` +
      `&service_dune=${services.dune ? 1 : 0}`;

    onApplyFilters(filterString);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      p={2}
      mb={2}
      sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
    >
      {/* Верхняя панель с поиском и кнопкой */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
      >
        {/* Левая часть — поиск */}
        <Box display="flex" alignItems="center" gap={2}>
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" sx={{ mb: 0.5 }}>
              Поиск
            </Typography>
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              placeholder="Введите запрос"
              autoComplete="off"
              InputProps={{
                endAdornment: query && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      sx={{
                        color: "gray",
                        bgcolor: "rgba(0,0,0,0.1)",
                        borderRadius: "50%",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.2)" },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Правая часть — кнопка и мини-блок "Сервисы" */}
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              mb: 1,
              minHeight: 32,
              padding: "4px 10px",
              fontSize: "0.8rem",
            }}
          >
            Сформировать
          </Button>

          <Paper
            elevation={1}
            sx={{
              borderRadius: 1,
              p: 0,
              textAlign: "center",
              backgroundColor: "#fff",
              minWidth: 140, // уже в два раза
            }}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center" // ✅ выравнивание по центру по вертикали
              mb={0.5}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "primary.main",
                  mr: 0.5,
                }}
              >
                Сервисы
              </Typography>
              <Checkbox
                size="small"
                checked={allServices}
                onChange={handleAllServicesChange}
                sx={{ p: 0.2 }}
              />
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              alignItems="flex-end"
              gap={1}
              sx={{
                "& .MuiFormControlLabel-root": {
                  flexDirection: "column-reverse", // подписи сверху
                  alignItems: "center",
                  margin: 0,
                },
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.7rem",
                  lineHeight: 1,
                  marginBottom: "1px",
                },
                "& .MuiCheckbox-root": {
                  padding: "1px",
                },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={services.media}
                    onChange={() => handleServiceChange("media")}
                  />
                }
                label="MEDIA"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={services.portal}
                    onChange={() => handleServiceChange("portal")}
                  />
                }
                label="PORTAL"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={services.dune}
                    onChange={() => handleServiceChange("dune")}
                  />
                }
                label="DUNE"
              />
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default MainBodyFilter;
